const MAX_LOGO_FILE_BYTES = 10 * 1024 * 1024;
const MAX_LOGO_WIDTH = 960;
const MAX_LOGO_HEIGHT = 320;
const SUPPORTED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The logo file could not be read.'));
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('The logo file could not be read.'));
    };
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The selected file is not a valid image.'));
    image.src = src;
  });
}

/** Validate and resize an uploaded header logo before persisting it in dashboard JSON. */
export async function prepareDashboardLogo(file: File): Promise<string> {
  if (!SUPPORTED_LOGO_TYPES.has(file.type)) {
    throw new Error('Choose a PNG, JPEG, or WebP image.');
  }
  if (file.size > MAX_LOGO_FILE_BYTES) {
    throw new Error('Logo images must be 10 MB or smaller.');
  }

  const source = await readAsDataUrl(file);
  const image = await loadImage(source);
  if (!image.naturalWidth || !image.naturalHeight) {
    throw new Error('The selected image has invalid dimensions.');
  }

  const scale = Math.min(
    1,
    MAX_LOGO_WIDTH / image.naturalWidth,
    MAX_LOGO_HEIGHT / image.naturalHeight,
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser could not process the logo image.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
}
